import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft, 
  Users, 
  WarningCircle, 
  Coins, 
  // Sparkle,
  Check
} from "@phosphor-icons/react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

// Step Types & Interfaces
type SurveyStep = 1 | 2 | 3 | 4 | 5;

interface SurveyAnswers {
  companySize: string;
  painPoints: string[];
  financialWellness: string;
  bnplInterest: string;
}

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 60 : -60,
    opacity: 0,
  }),
};

const PlatformSurvey = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<SurveyStep>(1);
  const [direction, setDirection] = useState<number>(1);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);

  // Survey state
  const [answers, setAnswers] = useState<SurveyAnswers>({
    companySize: "",
    painPoints: [],
    financialWellness: "",
    bnplInterest: "",
  });

  // Step 5: Redirect timer
  useEffect(() => {
    if (step === 5) {
      setIsRedirecting(true);
      const timer = setTimeout(() => {
        // Redirect to onboarding/registration with prefilled survey data
        navigate("/hr-registration", { 
          state: { 
            employees: answers.companySize,
            surveyCompleted: true,
            answers 
          } 
        });
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, [step, navigate, answers]);

  const nextStep = () => {
    if (step < 5) {
      setDirection(1);
      setStep((prev) => (prev + 1) as SurveyStep);
    }
  };

  const prevStep = () => {
    if (step > 1 && step < 5) {
      setDirection(-1);
      setStep((prev) => (prev - 1) as SurveyStep);
    }
  };

  const handleCompanySizeSelect = (option: string) => {
    setAnswers((prev) => ({ ...prev, companySize: option }));
    nextStep();
  };

  const handlePainPointToggle = (option: string) => {
    setAnswers((prev) => {
      const exists = prev.painPoints.includes(option);
      const updated = exists
        ? prev.painPoints.filter((item) => item !== option)
        : [...prev.painPoints, option];
      return { ...prev, painPoints: updated };
    });
  };

  const handleFinancialWellnessSelect = (option: string) => {
    setAnswers((prev) => ({ ...prev, financialWellness: option }));
    nextStep();
  };

  const handleBnplInterestSelect = (option: string) => {
    setAnswers((prev) => ({ ...prev, bnplInterest: option }));
    nextStep();
  };

  // Progress percentage calculation
  const totalQuestions = 4;
  const progressPercentage = Math.min(100, Math.round(((step - 1) / totalQuestions) * 100));

  return (
    <div className="bg-gray-50 min-h-screen text-gray-800 flex flex-col justify-between font-aeonic">
      <Navbar />

      <main className="flex-1 px-4 sm:px-6 py-12 md:py-20 bg-gradient-to-b from-visaro-orange-15 via-white to-gray-50 flex items-center justify-center relative">
        <div className="w-full max-w-2xl mx-auto">
          
          {/* Main Clean Elevated Visaro Card */}
          <div className="relative rounded-3xl bg-white border border-gray-200/90 p-6 sm:p-10 md:p-12 shadow-xl shadow-gray-200/60 overflow-hidden">
            
            {/* Top Decorative Brand Gradient Line */}
            {/* <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-visaro-orange via-visaro-orange to-visaro-visaGreen" /> */}

            {/* Top Progress Bar & Header (Steps 1 to 4) */}
            {step < 5 && (
              <div className="mb-8 sm:mb-10">
                <div className="flex items-center justify-between text-xs sm:text-sm font-semibold text-gray-500 mb-3">
                  <span className="flex items-center gap-2">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-visaro-orange-15 text-visaro-orange text-xs font-bold border border-visaro-orange/30">
                      {step}
                    </span>
                    Step {step} of {totalQuestions}
                  </span>
                  <span className="text-visaro-orange font-bold tracking-wide">
                    {progressPercentage}% Completed
                  </span>
                </div>
                
                {/* Progress Track */}
                <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden p-0.5 border border-gray-200/80">
                  <motion.div 
                    className="h-full bg-gradient-to-r from-visaro-orange to-deepBlue rounded-full"
                    initial={{ width: "0%" }}
                    animate={{ width: `${progressPercentage}%` }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                  />
                </div>
              </div>
            )}

            {isRedirecting && <></>}

            {/* Question Slider Container with Framer Motion */}
            <div className="relative min-h-[340px] flex flex-col justify-between">
              <AnimatePresence mode="wait" custom={direction}>
                
                {/* STEP 1: Company Size */}
                {step === 1 && (
                  <motion.div
                    key="step-1"
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="flex flex-col gap-6"
                  >
                    <div>
                      <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-visaro-orange-15 border border-visaro-orange/30 text-xs text-visaro-orange mb-3 font-bold">
                        <Users className="w-4 h-4 text-visaro-orange" />
                        <span>COMPANY SIZE</span>
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-deepBlue leading-tight font-aeonic">
                        How many employees are currently in your organization?
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mt-2">
                      {["1-49", "50-99", "100-499", "500+"].map((option) => {
                        const isSelected = answers.companySize === option;
                        return (
                          <button
                            key={option}
                            type="button"
                            onClick={() => handleCompanySizeSelect(option)}
                            className={`group relative flex items-center justify-between p-4 sm:p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                              isSelected
                                ? "bg-visaro-orange-15/60 border-visaro-orange shadow-md shadow-visaro-orange/10 scale-[1.01]"
                                : "bg-white border-gray-200/90 hover:border-visaro-orange hover:bg-visaro-orange-15/20 hover:scale-[1.01]"
                            }`}
                          >
                            <span className={`text-base sm:text-lg font-bold transition-colors ${
                              isSelected ? "text-visaro-orange" : "text-deepBlue group-hover:text-visaro-orange"
                            }`}>
                              {option} employees
                            </span>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                              isSelected ? "border-visaro-orange bg-visaro-orange text-white" : "border-gray-300 group-hover:border-visaro-orange"
                            }`}>
                              {isSelected && <Check className="w-4 h-4 stroke-[3]" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {/* STEP 2: Current HR Pain Points (Multi-select) */}
                {step === 2 && (
                  <motion.div
                    key="step-2"
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="flex flex-col gap-6"
                  >
                    <div>
                      <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-visaro-orange-15 border border-visaro-orange/30 text-xs text-visaro-orange mb-3 font-bold">
                        <WarningCircle className="w-4 h-4 text-visaro-orange" />
                        <span>HR CHALLENGES</span>
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-deepBlue leading-tight font-aeonic">
                        What is your biggest HR challenge right now?
                      </h2>
                      <p className="text-xs sm:text-sm text-gray-500 mt-1">
                        Select all that apply to your team.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-3 mt-1">
                      {[
                        "Managing Payroll & Deductions",
                        "Tracking Leave & Attendance",
                        "Employee Retention & Morale",
                        "Paying for Expensive HR Software",
                      ].map((option) => {
                        const isSelected = answers.painPoints.includes(option);
                        return (
                          <button
                            key={option}
                            type="button"
                            onClick={() => handlePainPointToggle(option)}
                            className={`group relative flex items-center justify-between p-4 sm:p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                              isSelected
                                ? "bg-visaro-white-15/60 border-visaro-orange shadow-md shadow-visaro-orange/10 scale-[1.01]"
                                : "bg-white border-gray-200/90 hover:border-visaro-orange hover:bg-visaro-orange-15/20 hover:scale-[1.01]"
                            }`}
                          >
                            <span className={`text-sm sm:text-base font-semibold transition-colors ${
                              isSelected ? "text-visaro-orange font-bold" : "text-deepBlue group-hover:text-visaro-orange"
                            }`}>
                              {option}
                            </span>
                            <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                              isSelected ? "border-visaro-orange bg-visaro-orange text-white" : "border-gray-300 group-hover:border-visaro-orange"
                            }`}>
                              {isSelected && <Check className="w-4 h-4 stroke-[3]" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Step 2 Continue Button */}
                    <div className="flex justify-end pt-2">
                      <button
                        type="button"
                        onClick={nextStep}
                        disabled={answers.painPoints.length === 0}
                        className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold rounded-xl text-white bg-deepBlue hover:bg-opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-md"
                      >
                        <span>Continue</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* STEP 3: Employee Financial Wellness */}
                {step === 3 && (
                  <motion.div
                    key="step-3"
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="flex flex-col gap-6"
                  >
                    <div>
                      <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-visaro-orange-15 border border-visaro-orange/30 text-xs text-visaro-orange mb-3 font-bold">
                        <Coins className="w-4 h-4 text-visaro-orange" />
                        <span>FINANCIAL WELLNESS</span>
                      </div>
                      <h2 className="text-2xl sm:text-3xl font-bold text-deepBlue leading-tight font-aeonic">
                        Do your employees frequently request salary advances or emergency loans?
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 gap-3 mt-1">
                      {[
                        "Yes, all the time",
                        "Occasionally",
                        "Rarely",
                        "We don't offer advances",
                      ].map((option) => {
                        const isSelected = answers.financialWellness === option;
                        return (
                          <button
                            key={option}
                            type="button"
                            onClick={() => handleFinancialWellnessSelect(option)}
                            className={`group relative flex items-center justify-between p-4 sm:p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                              isSelected
                                ? "bg-visaro-orange-15/60 border-visaro-orange shadow-md shadow-visaro-orange/10 scale-[1.01]"
                                : "bg-white border-gray-200/90 hover:border-visaro-orange hover:bg-visaro-orange-15/20 hover:scale-[1.01]"
                            }`}
                          >
                            <span className={`text-sm sm:text-base font-semibold transition-colors ${
                              isSelected ? "text-visaro-orange font-bold" : "text-deepBlue group-hover:text-visaro-orange"
                            }`}>
                              {option}
                            </span>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                              isSelected ? "border-visaro-orange bg-visaro-orange text-white" : "border-gray-300 group-hover:border-visaro-orange"
                            }`}>
                              {isSelected && <Check className="w-4 h-4 stroke-[3]" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {/* STEP 4: The Pitch / BNPL Interest */}
                {step === 4 && (
                  <motion.div
                    key="step-4"
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.35, ease: "easeInOut" }}
                    className="flex flex-col gap-6"
                  >
                    <div>
                      {/* <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-visaro-orange-15 border border-visaro-orange/30 text-xs text-visaro-orange mb-3 font-bold">
                        <Sparkle className="w-4 h-4 text-visaro-orange" />
                        <span>THE SOLUTION</span>
                      </div> */}
                      <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-deepBlue leading-tight font-aeonic">
                        If you could get a world-class HR Management System for <span className="text-visaro-orange">FREE</span>, with a built-in 'Credit on Request' (BNPL) feature for your employees at <span className="text-visaro-visaGreen">ZERO risk</span> to your company, would you switch?
                      </h2>
                    </div>

                    <div className="grid grid-cols-1 gap-3 mt-1">
                      {[
                        "Absolutely, where do I sign up?",
                        "I need to see a demo first",
                        "Not right now",
                      ].map((option) => {
                        const isSelected = answers.bnplInterest === option;
                        return (
                          <button
                            key={option}
                            type="button"
                            onClick={() => handleBnplInterestSelect(option)}
                            className={`group relative flex items-center justify-between p-4 sm:p-5 rounded-2xl border-2 text-left transition-all duration-200 ${
                              isSelected
                                ? "bg-visaro-orange-15/60 border-visaro-orange shadow-md shadow-visaro-orange/10 scale-[1.01]"
                                : "bg-white border-gray-200/90 hover:border-visaro-orange hover:bg-visaro-orange-15/20 hover:scale-[1.01]"
                            }`}
                          >
                            <span className={`text-sm sm:text-base font-semibold transition-colors ${
                              isSelected ? "text-visaro-orange font-bold" : "text-deepBlue group-hover:text-visaro-orange"
                            }`}>
                              {option}
                            </span>
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                              isSelected ? "border-visaro-orange bg-visaro-orange text-white" : "border-gray-300 group-hover:border-visaro-orange"
                            }`}>
                              {isSelected && <Check className="w-4 h-4 stroke-[3]" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                {/* STEP 5: Completion & Redirect Screen */}
                {step === 5 && (
                  <motion.div
                    key="step-5"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex flex-col items-center justify-center text-center py-8 gap-6"
                  >
                    {/* Mint Green Checkmark Badge */}
                    <div className="relative flex items-center justify-center">
                      <div className="w-20 h-20 rounded-full bg-visaro-visaGreen/20 flex items-center justify-center text-visaro-visaGreen border border-visaro-visaGreen/40 shadow-lg shadow-visaro-visaGreen/10">
                        <CheckCircle size={56} weight="fill" className="text-visaro-visaGreen" />
                      </div>
                      <div className="absolute inset-0 rounded-full border-2 border-visaro-visaGreen border-t-transparent animate-spin p-1" />
                    </div>

                    <div className="flex flex-col gap-2">
                      <h2 className="text-2xl sm:text-3xl font-bold text-deepBlue">
                        Thank you!
                      </h2>
                      <p className="text-base sm:text-lg text-gray-600 font-medium">
                        We are preparing your free portal...
                      </p>
                    </div>

                    {/* Brief Spinner & Progress Indicator */}
                    <div className="flex items-center gap-3 px-4 py-2 rounded-full bg-gray-100 border border-gray-200 text-xs text-gray-500 mt-2">
                      <svg className="h-4 w-4 animate-spin text-visaro-orange" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      <span>Redirecting to onboarding portal...</span>
                    </div>
                  </motion.div>
                )}

              </AnimatePresence>
            </div>

            {/* Card Footer Back Button (Steps 2 to 4) */}
            {step > 1 && step < 5 && (
              <div className="mt-8 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex items-center gap-1.5 text-gray-600 hover:text-deepBlue font-medium transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Previous question</span>
                </button>
                <span className="text-gray-400">Visaro Platform Survey</span>
              </div>
            )}

          </div>

          {/* Optional Footer Link */}
          <div className="text-center mt-6">
            <Link to="/hr-registration" className="text-xs text-gray-500 hover:text-visaro-orange underline transition-colors">
              Skip survey and go directly to onboarding →
            </Link>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PlatformSurvey;
